import React, { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, Users, Clock, Target, Lightbulb } from 'lucide-react';
import { User } from '../../types';

interface SmartRecommendationsProps {
  currentUser: User;
  deployments: any[];
}

interface Recommendation {
  id: string;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  category: string;
  action?: string;
  icon: any;
}

const SmartRecommendations: React.FC<SmartRecommendationsProps> = ({ currentUser, deployments }) => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);

  useEffect(() => {
    generateRecommendations();
  }, [deployments, currentUser]);

  const generateRecommendations = () => {
    const recs: Recommendation[] = [];

    if (!deployments || deployments.length === 0) {
      recs.push({
        id: '1',
        title: 'Start Your First Deployment',
        description: 'Begin by creating your first deployment request. Our system will guide you through the process.',
        impact: 'high',
        category: 'Getting Started',
        action: 'Create Deployment',
        icon: Target
      });
      setRecommendations(recs);
      return;
    }

    // Analyze pending deployments
    const pending = deployments.filter(d => d.status === 'pending');
    if (pending.length > 3) {
      recs.push({
        id: 'pending-alert',
        title: 'High Pending Deployments',
        description: `You have ${pending.length} deployments waiting for approval. Consider following up with team leads or consolidating similar deployments.`,
        impact: 'high',
        category: 'Workflow',
        action: 'View Pending',
        icon: Clock
      });
    }

    // Check for rolled-back deployments — deployments never actually get a
    // 'failed' status in this app's real lifecycle, only 'rolled_back'.
    const failed = deployments.filter(d => d.status === 'rolled_back');
    if (failed.length > 0) {
      recs.push({
        id: 'failed-deployments',
        title: 'Review Rolled-Back Deployments',
        description: `${failed.length} deployment(s) were rolled back. Review logs and error messages to identify root causes and prevent future rollbacks.`,
        impact: 'high',
        category: 'Quality',
        action: 'View Failures',
        icon: TrendingUp
      });
    }

    // Team collaboration
    const userDeployments = deployments.filter((d: any) => d.requestedBy?.id === currentUser.id);
    if (userDeployments.length > 10 && currentUser.role === 'developer') {
      recs.push({
        id: 'collaboration',
        title: 'Collaborate More Effectively',
        description: 'Use the chat feature to communicate with team members about deployments and get faster approvals.',
        impact: 'medium',
        category: 'Collaboration',
        action: 'Open Chat',
        icon: Users
      });
    }

    // Timing optimization
    const recentDeployments = deployments.filter(d => {
      const hoursSince = (Date.now() - new Date(d.createdAt).getTime()) / (1000 * 60 * 60);
      return hoursSince <= 24;
    });

    if (recentDeployments.length > 5) {
      recs.push({
        id: 'timing',
        title: 'Optimize Deployment Timing',
        description: 'Schedule deployments during off-peak hours (late evening or early morning) to minimize user impact.',
        impact: 'medium',
        category: 'Best Practice',
        action: 'View Schedule',
        icon: Clock
      });
    }

    // Documentation
    const withoutDescription = deployments.filter((d: any) => !d.description || d.description.length < 20);
    if (withoutDescription.length > deployments.length * 0.3) {
      recs.push({
        id: 'documentation',
        title: 'Improve Documentation',
        description: 'Add detailed descriptions to your deployments. Good documentation helps reviewers approve faster and reduces deployment errors.',
        impact: 'low',
        category: 'Quality',
        action: 'Learn More',
        icon: Lightbulb
      });
    }

    // Priority management
    const criticalDeployments = deployments.filter(d => d.priority === 'critical');
    const mediumDeployments = deployments.filter(d => d.priority === 'medium' || !d.priority);
    
    if (criticalDeployments.length > mediumDeployments.length && deployments.length > 5) {
      recs.push({
        id: 'priority',
        title: 'Review Priority Levels',
        description: 'Too many critical deployments can dilute urgency. Reserve "critical" priority for true emergencies.',
        impact: 'low',
        category: 'Best Practice',
        action: 'Review Priorities',
        icon: Target
      });
    }

    // Success patterns
    const successRate = deployments.filter(d => d.status === 'deployed').length / Math.max(1, deployments.filter(d => ['deployed', 'rolled_back'].includes(d.status)).length);
    if (successRate > 0.9 && deployments.length > 10) {
      recs.push({
        id: 'excellence',
        title: 'Maintain Your Excellence',
        description: `Excellent work! Your ${(successRate * 100).toFixed(0)}% success rate is outstanding. Keep following your current practices.`,
        impact: 'low',
        category: 'Recognition',
        icon: Sparkles
      });
    }

    setRecommendations(recs);
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'bg-rose-100 text-rose-700 border-rose-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-sky-100 text-sky-700 border-sky-200';
    }
  };

  const getAccentByImpact = (impact: string) => {
    switch (impact) {
      case 'high': return 'border-l-4 border-l-rose-500';
      case 'medium': return 'border-l-4 border-l-amber-500';
      default: return 'border-l-4 border-l-sky-500';
    }
  };

  const getImpactBadge = (impact: string) => {
    switch (impact) {
      case 'high': return 'High Impact';
      case 'medium': return 'Medium Impact';
      default: return 'Low Impact';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-fuchsia-600 to-indigo-600 rounded-lg shadow-md shadow-fuchsia-200">
          <Sparkles className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Smart Recommendations</h2>
          <p className="text-gray-600">AI-powered suggestions to improve your workflow</p>
        </div>
      </div>

      {/* Recommendations Grid */}
      <div className="grid gap-4">
        {recommendations.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
            <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">No recommendations at this time.</p>
            <p className="text-sm text-gray-500 mt-1">Keep deploying and we'll provide smart insights!</p>
          </div>
        ) : (
          recommendations.map((rec) => {
            const Icon = rec.icon;
            return (
              <div
                key={rec.id}
                className={`bg-white rounded-xl shadow-md border border-gray-200 ${getAccentByImpact(rec.impact)} p-6 hover:shadow-lg transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gradient-to-br from-fuchsia-100 to-indigo-100 rounded-lg">
                    <Icon className="h-6 w-6 text-fuchsia-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{rec.title}</h3>
                      <span className={`text-xs font-medium px-3 py-1 rounded-full border ${getImpactColor(rec.impact)}`}>
                        {getImpactBadge(rec.impact)}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{rec.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {rec.category}
                      </span>
                      {/* The per-recommendation action buttons (e.g. "View Failures")
                          had no click handler and did nothing, so they were removed
                          rather than left looking clickable. */}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Stats */}
      <div className="bg-gradient-to-r from-fuchsia-50 via-purple-50 to-indigo-50 rounded-xl p-6 border border-fuchsia-200">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-fuchsia-600" />
          Recommendation Engine Stats
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white p-3 rounded-lg shadow-sm">
            <p className="text-xs text-gray-600 mb-1">Active Suggestions</p>
            <p className="text-2xl font-bold text-fuchsia-600">{recommendations.length}</p>
          </div>
          <div className="bg-white p-3 rounded-lg shadow-sm">
            <p className="text-xs text-gray-600 mb-1">High Priority</p>
            <p className="text-2xl font-bold text-rose-600">
              {recommendations.filter(r => r.impact === 'high').length}
            </p>
          </div>
          <div className="bg-white p-3 rounded-lg shadow-sm">
            <p className="text-xs text-gray-600 mb-1">Data Analyzed</p>
            <p className="text-2xl font-bold text-indigo-600">{deployments.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartRecommendations;
